import { useEffect, useState } from "react";
import { useSafeApiCall, useApiClient } from "src/hooks/api";
import { API_PREFIX_PATH } from "src/constants";
import { createIsoDate } from "../../utils";
import { zeroDate } from "../../constants";
import {
  type Employee,
  type ActivityLog,
  type NoteRequest,
} from "src/vibe-code/rcm-v2/types";

export const useEmployees = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const { fetchFromApi } = useApiClient();
  const safeApiCall = useSafeApiCall();

  const getEmployees = () => {
    safeApiCall(() => fetchFromApi(`${API_PREFIX_PATH}/dealer-employees`), {
      suppressErrorRedirect: true,
    })
      .then((response) => {
        setEmployees(response as Employee[]);
      })
      .catch((err) => console.log(err));
  };

  const updateEmployee = (data: any) => {
    let birthdayFormatted = zeroDate;
    if (data.birthday) {
      const [year, month, day] = data.birthday.split("-");
      birthdayFormatted = createIsoDate(year, month, day);
    }
    const cleanedData = {
      additional_emails: data.additionalEmailAddresses,
      birthday: birthdayFormatted,
      email: data.email,
      first_name: data.employeeFirstName,
      last_name: data.employeeLastName,
      phone_number: data.directLine,
      profile_notes: data.employeeProfileNotes,
      role: data.role,
      title: data.title,
      gift_sent: data.giftSent,
    };
    safeApiCall(
      () =>
        fetchFromApi(`${API_PREFIX_PATH}/dealer-employees/${data.id}`, {
          method: "PUT",
          body: cleanedData,
        }),
      {
        suppressErrorRedirect: true,
        useServerErrorAsToast: true,
      },
    )
      .then(() => {
        getEmployees();
      })
      .catch((err) => console.log(err));
  };
  const createEmployee = (data: any) => {
    let birthdayFormatted = zeroDate;
    if (data.birthday) {
      const [year, month, day] = data.birthday.split("-");
      birthdayFormatted = createIsoDate(year, month, day);
    }
    const cleanedData = {
      additional_emails: data.additionalEmailAddresses,
      birthday: birthdayFormatted,
      dealer_id: data.dealerId,
      email: data.email,
      first_name: data.employeeFirstName,
      last_name: data.employeeLastName,
      phone_number: data.directLine,
      profile_notes: data.employeeProfileNotes,
      role: data.role,
      title: data.title,
      gift_sent: data.giftSent,
    };
    safeApiCall(
      () =>
        fetchFromApi(`${API_PREFIX_PATH}/dealer-employees`, {
          method: "POST",
          body: cleanedData,
        }),
      {
        suppressErrorRedirect: true,
        useServerErrorAsToast: true,
      },
    )
      .then(() => {
        getEmployees();
      })
      .catch((err) => console.log(err));
  };

  const deleteEmployee = (employeeId: string) => {
    safeApiCall(
      () =>
        fetchFromApi(`${API_PREFIX_PATH}/dealer-employees/${employeeId}`, {
          method: "DELETE",
        }),
      {
        suppressErrorRedirect: true,
        useServerErrorAsToast: true,
      },
    )
      .then(() => {
        getEmployees();
        return true;
      })
      .catch((err) => {
        console.log(err);
        return false;
      });
  };

  useEffect(() => {
    getEmployees();
  }, []);

  return {
    employees,
    getEmployees,
    createEmployee,
    updateEmployee,
    deleteEmployee,
  };
};

export const useEmployeeDetails = () => {
  const { fetchFromApi } = useApiClient();
  const safeApiCall = useSafeApiCall();

  const getEmployeeActivityLogs = async (
    employeeId: string,
  ): Promise<ActivityLog[]> => {
    try {
      const response = await safeApiCall(
        () =>
          fetchFromApi(
            `${API_PREFIX_PATH}/dealer-employees/${employeeId}/activity-logs`,
          ),
        { suppressErrorRedirect: true },
      );
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error("Error fetching employee activity logs:", error);
      return [];
    }
  };

  const createEmployeeNote = async (
    employeeId: string,
    noteRequest: NoteRequest,
  ): Promise<boolean> => {
    try {
      await safeApiCall(
        () =>
          fetchFromApi(
            `${API_PREFIX_PATH}/dealer-employees/${employeeId}/activity-logs/note`,
            {
              method: "POST",
              body: noteRequest,
            },
          ),
        {
          suppressErrorRedirect: true,
          useServerErrorAsToast: true,
        },
      );
      return true;
    } catch (error) {
      console.error("Error creating employee note:", error);
      return false;
    }
  };

  return {
    getEmployeeActivityLogs,
    createEmployeeNote,
  };
};
